import { RootState } from 'store/reducer';
import { FloorPlanAppInstanceConfigState } from 'store/floorplan-app-instance-config/types';
import { selectCurrentAppInstanceConfig } from 'store/app-instance-configs/selectors';
import { AppInstanceConfig, NativeResourceBookingConfig } from 'store/app-instance-configs/types';
import { ResourceBookingEnum } from 'store/app-instance-configs/enums';
import { createSelector } from 'reselect';

export const floorplanAppInstanceConfigSelector = (state: RootState): FloorPlanAppInstanceConfigState =>
  state.floorplanAppInstanceConfig;

export const selectFloorPlanAppInstanceConfig = createSelector(
  [floorplanAppInstanceConfigSelector],
  config => config.data ?? null,
);

export const selectFloorPlanAppInstanceConfigStatus = createSelector(
  [floorplanAppInstanceConfigSelector],
  configs => configs.getAppInstanceConfig.status,
);

export const selectFloorPlanAppInstanceConfigError = createSelector(
  [floorplanAppInstanceConfigSelector],
  configs => configs.getAppInstanceConfig.error,
);

export interface CoworkerConfigFlags {
  nameRevealed: boolean;
  companyRevealed: boolean;
  teamsConfigured: boolean;
}

export const COWORKER_FLAGS_ALL_OFF: CoworkerConfigFlags = {
  nameRevealed: false,
  companyRevealed: false,
  teamsConfigured: false,
};

/**
 * The app instance config the coworker fields are read from.
 *
 * The resources page (where the native floorplan lives) never requests `floorplanAppInstanceConfig`
 * — that slice is filled only by the legacy Archilogic floorplan page — so the building's config is
 * taken from the app-instance-configs the app loads on start: the current one is the config the page
 * is browsing under. The legacy slice stays as a fallback for the legacy page.
 */
export const selectCoworkerAppInstanceConfig = createSelector(
  [selectCurrentAppInstanceConfig, selectFloorPlanAppInstanceConfig],
  (currentConfig, floorplanConfig): AppInstanceConfig | null => currentConfig ?? floorplanConfig ?? null,
);

/**
 * Only the native adapter's config carries the coworker fields. The store keeps the response JSON
 * as-is, so the discriminator is checked rather than assumed — a non-native config that happens to
 * carry `coworker_*` keys must not switch the feature on. The adapter is read from the config blob
 * when it names one, otherwise from the app instance's vertical adapter (what the rest of the app
 * discriminates on).
 */
const nativeConfigOf = (appInstanceConfig: AppInstanceConfig | null): NativeResourceBookingConfig | null => {
  const config = appInstanceConfig?.config;

  if (!config) {
    return null;
  }

  const adapter = 'adapter' in config && config.adapter ? config.adapter : appInstanceConfig?.vertical_adapter?.name;

  return adapter === ResourceBookingEnum.NativeAdapter ? (config as NativeResourceBookingConfig) : null;
};

export const selectCoworkerConfigFlags = createSelector(
  [selectCoworkerAppInstanceConfig],
  (appInstanceConfig): CoworkerConfigFlags => {
    const config = nativeConfigOf(appInstanceConfig);

    if (!config || config.coworker_visibility_enabled !== true) {
      return COWORKER_FLAGS_ALL_OFF;
    }

    const nameRevealed = config.coworker_show_name === true;

    return {
      nameRevealed,
      companyRevealed: config.coworker_show_company === true,
      teamsConfigured: nameRevealed && config.coworker_teams_enabled === true,
    };
  },
);
